<script setup lang="ts">
import { useAppStore } from "@/store/app";
import type {
  PictureChoiceElement,
  PictureChoiceOption,
} from "@/types/pictureChoice";
import { createPictureChoiceOtherOption } from "@/util/pictureChoice";
import { UICollapseItem, UIInput } from "@gohighlevel/ghl-ui";
import { cloneDeep } from "lodash";
import { computed, onBeforeUnmount, reactive, type PropType } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  element: {
    type: Object as PropType<PictureChoiceElement>,
    required: true,
  },
});

const emit = defineEmits<{
  (event: "update:element", element: PictureChoiceElement): void;
}>();

const store = useAppStore();
const { t } = useI18n();
let hasUncommittedAltTextChanges = false;
const altTextDrafts = reactive<Record<number, string>>({});

const altTextOptions = computed(() => {
  const options = props.element.picklistOptionsImage.map((option, index) => ({
    option,
    index,
    isOther: false,
  }));

  if (
    props.element.dataType !== "CHECKBOX" &&
    props.element.allowCustomOption
  ) {
    options.push({
      option: createPictureChoiceOtherOption(),
      index: props.element.picklistOptionsImage.length,
      isOther: true,
    });
  }

  return options;
});

const updateAltText = (index: number, isOther: boolean, value: string) => {
  if (isOther) return;

  altTextDrafts[index] = value;

  const element = cloneDeep(props.element);
  const option: PictureChoiceOption | undefined =
    element.picklistOptionsImage[index];

  if (!option) return;

  const altText = value.trim();
  const nextAltText = altText || option.label;
  const nextAltTextSource = altText ? "CUSTOM" : "AUTO";
  if (
    option.altText === nextAltText &&
    option.altTextSource === nextAltTextSource
  ) {
    return;
  }

  option.altText = nextAltText;
  option.altTextSource = nextAltTextSource;

  emit("update:element", element);
  store.anyUnsavedChanges = true;
  hasUncommittedAltTextChanges = true;
};

const getAltTextValue = (index: number, option: PictureChoiceOption) =>
  Object.prototype.hasOwnProperty.call(altTextDrafts, index)
    ? altTextDrafts[index]
    : option.altText ?? option.label;

const handleAltTextBlur = (index: number) => {
  delete altTextDrafts[index];
  commitAltTextHistory();
};

const commitAltTextHistory = () => {
  if (!hasUncommittedAltTextChanges) return;

  hasUncommittedAltTextChanges = false;
  store.addToHistory();
};

onBeforeUnmount(commitAltTextHistory);
</script>

<template>
  <UICollapseItem
    id="picture-choice-alt-text-settings"
    data-testid="picture-choice-alt-text-settings"
    :name="t('fields.choiceElements.pictureChoice.altText')"
    :title="t('fields.choiceElements.pictureChoice.altText')"
  >
    <div class="space-y-4">
      <div
        v-for="{ option, index, isOther } in altTextOptions"
        :key="`${isOther ? 'other' : index}-${option.label}`"
        class="space-y-1.5"
      >
        <label
          :for="`picture-choice-alt-text-${index}`"
          class="block text-sm font-medium text-gray-700"
        >
          {{ option.label }}
        </label>
        <UIInput
          :id="`picture-choice-alt-text-${index}`"
          :data-testid="`picture-choice-alt-text-${index}`"
          :model-value="getAltTextValue(index, option)"
          :placeholder="option.label"
          :disabled="isOther"
          @update:model-value="updateAltText(index, isOther, $event)"
          @blur="handleAltTextBlur(index)"
        />
      </div>
    </div>
  </UICollapseItem>
</template>
